import ShipmentEntrySectionAction from '@/features/assets/components/shipment-entry/ShipmentEntrySectionAction.tsx';
import ShipmentEntrySectionAsset from '@/features/assets/components/shipment-entry/ShipmentEntrySectionAsset.tsx';
import ShipmentEntrySectionTrackingNumber from '@/features/assets/components/shipment-entry/ShipmentEntrySectionTrackingNumber.tsx';
import ShipmentEntrySectionValidation from '@/features/assets/components/shipment-entry/ShipmentEntrySectionValidation.tsx';
import { normalizeApiError } from '@/common/errors/api-error.ts';
import { captureWithContext } from '@/common/telemetry/sentry.ts';
import {
  getAssetControllerReturnAssetMutationOptions,
  getAssetControllerShipAssetMutationOptions,
} from '@/generated/api/asset.ts';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  AppShell,
  Box,
  Button,
  Group,
  Stack,
  Text,
} from '@mantine/core';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createFileRoute,
  useBlocker,
  useNavigate,
} from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

const searchSchema = z.object({
  assetId: z.coerce.string().optional(),
  action: z.enum(['ShipToJobSite', 'ReturnToWorldLink']).optional(),
});

const formSchema = z.object({
  assetId: z.string().nonempty(),
  action: z.enum(['ShipToJobSite', 'ReturnToWorldLink']),
  trackingNumber: z.string().nonempty(),
  force: z.boolean(),
});

export type FormValues = z.infer<typeof formSchema>;

function getShipmentSubmissionErrorMessage(error: unknown) {
  const normalized = normalizeApiError(error);

  switch (normalized.message) {
    case 'ASSET_IN_TRANSIT':
      return 'This asset already has an in-transit shipment. Wait for it to complete or resolve the existing shipment before creating another.';
    case 'ASSET_NOT_FOUND':
      return 'The selected asset could not be found. Refresh the page and choose the asset again.';
    case 'INACTIVE_ASSET':
      return 'This asset is not active and cannot be shipped.';
    case 'WORK_ORDER_NOT_FOUND':
      return 'No matching work order could be inferred for this tracking number.';
    case 'MULTIPLE_WORK_ORDERS_FOUND':
      return 'More than one work order matched this tracking number. The system cannot infer the destination safely.';
    case 'ORIGIN_MATCHES_DESTINATION':
      return 'The inferred origin and destination are the same, so this shipment cannot be added.';
    case 'FAILED_TO_CREATE_SHIPMENT':
      return 'The carrier shipment could not be loaded from the tracking number. Check the tracking number and try again.';
    case 'SHIPPING_RATES_UNAVAILABLE':
      return 'The shipment was rejected because rates could not be calculated.';
    case 'ADDRESS_NOT_FOUND':
      return 'A required origin or destination address could not be found.';
    default:
      return normalized.message || 'Shipment entry failed. Please try again.';
  }
}

export const Route = createFileRoute('/assets/add-shipment')({
  component: RouteComponent,
  staticData: {
    authorization: {
      permission: {
        asset: ['write'],
      },
    },
  },
  validateSearch: searchSchema,
});

// eslint-disable-next-line react-refresh/only-export-components
function RouteComponent() {
  const { assetId, action } = Route.useSearch();

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [submissionError, setSubmissionError] = useState<{
    message: string;
    eventId?: string;
  } | null>(null);

  const submissionMutation = useMutation(
    getAssetControllerShipAssetMutationOptions(),
  );
  const returnToWorldLinkMutation = useMutation(
    getAssetControllerReturnAssetMutationOptions(),
  );

  const methods = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      assetId: assetId ?? '',
      action: action ?? 'ShipToJobSite',
      force: false,
    },
  });

  const onSubmit = async (data: FormValues) => {
    setSubmissionError(null);

    const mutation =
      data.action === 'ReturnToWorldLink'
        ? returnToWorldLinkMutation
        : submissionMutation;

    try {
      await mutation.mutateAsync({
        data: {
          assetId: data.assetId,
          trackingNumber: data.trackingNumber,
          force: data.force,
        },
      });
    } catch (error) {
      const normalized = normalizeApiError(error);
      const eventId =
        normalized.kind === 'validation' || normalized.kind === 'notFound'
          ? undefined
          : captureWithContext(error, {
              route: '/assets/add-shipment',
              operation: 'assets.shipment.submit',
            });

      setSubmissionError({
        message: getShipmentSubmissionErrorMessage(error),
        eventId,
      });
      return;
    }

    await queryClient.invalidateQueries({ queryKey: [`/api/v1/assets/query`] });

    await navigate({ to: '/assets' });
  };

  // eslint-disable-next-line react-hooks/incompatible-library
  const watched_assetId = methods.watch('assetId');
  const watched_action = methods.watch('action');
  const watched_trackingNumber = methods.watch('trackingNumber');
  const isDirty = methods.formState.isDirty;

  useEffect(() => {
    setSubmissionError(null);
  }, [watched_action, watched_assetId, watched_trackingNumber]);

  useBlocker({
    shouldBlockFn: () => {
      if (!isDirty) return false;
      const shouldLeave = window.confirm(
        'You have unsaved shipment details. Leave this page?',
      );
      return !shouldLeave;
    },
    enableBeforeUnload: () => isDirty,
  });

  return (
    <AppShell header={{ height: 48 }} padding={0}>
      <AppShell.Header
        style={{
          borderBottom: '1px solid var(--mantine-color-default-border)',
          backgroundColor: 'var(--mantine-color-default)',
        }}
      >
        <Group h="100%" px="sm" justify="space-between" wrap="nowrap">
          <Group gap="md" wrap="nowrap">
            <Button
              variant="transparent"
              size="compact-lg"
              p={0}
              aria-label="Back to Assets"
              onClick={() => navigate({ to: '/assets' })}
            >
              <ArrowLeftIcon />
            </Button>
            <Text fw={700} size="sm">
              Create Shipment Entry
            </Text>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Main
        style={{
          height: '100dvh',
          overflow: 'hidden',
          backgroundColor: 'var(--mantine-color-body)',
        }}
      >
        <Box h="100%" style={{ minHeight: 0 }} p="sm">
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <Stack gap="sm">
                <ShipmentEntrySectionAsset />
                {watched_assetId ? <ShipmentEntrySectionAction /> : null}
                {watched_assetId && watched_action ? (
                  <ShipmentEntrySectionTrackingNumber />
                ) : null}
                {submissionError ? (
                  <Alert color="red" title="Shipment entry failed">
                    <Stack gap={4}>
                      <Text size="sm">{submissionError.message}</Text>
                      {submissionError.eventId ? (
                        <Text size="xs" c="dimmed">
                          Event ID: {submissionError.eventId}
                        </Text>
                      ) : null}
                    </Stack>
                  </Alert>
                ) : null}
                {watched_assetId && watched_action && watched_trackingNumber ? (
                  <ShipmentEntrySectionValidation />
                ) : null}
              </Stack>
            </form>
          </FormProvider>
        </Box>
      </AppShell.Main>
    </AppShell>
  );
}
